import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { sendTransferEmails } from '../../lib/TransferEmail';
import moment from 'moment';
import '../../styles/detail.css';

const UserLoanDetails = ({ user, userId, setUser }) => {
  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const loanData = user?.loanDetails;

  const [updated, setUpdated] = useState({
    amount: loanData?.amount || '',
    date: loanData?.date || '',
    duration: loanData?.duration || '',
    otherBanks: loanData?.otherBanks || '',
    reason: loanData?.reason || '',
    status: loanData?.status || 'pending',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdated({ ...updated, [name]: value });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const payload = { ...updated };
      await updateDoc(doc(db, 'users', userId), { loanDetails: payload });
      setUser({ ...user, loanDetails: payload });
      setEditMode(false);
      setSuccessMessage('Loan details updated');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(`Error saving: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const [isApproving, setIsApproving] = useState(false);

  const handleApprove = async () => {
    if (!window.confirm("Approve this loan? This will credit the loan amount to the user.")) return;
    setIsApproving(true);
    setError('');
    setSuccessMessage('');
    try {
      const transaction = {
        amount: Number(loanData?.amount) || 0,
        description: "Loan approved",
        date: moment().format('MMMM D, YYYY'),
        type: "credit",
        beneficiary: "Loan",
        account: "Savings",
      };
      const existingTransactions = user.transactions || [];
      await updateDoc(doc(db, 'users', userId), {
        transactions: [...existingTransactions, transaction],
        loanDetails: null,
      });
      setUser({
        ...user,
        transactions: [...existingTransactions, transaction],
        loanDetails: null,
      });
      sendTransferEmails(user.email, {
        sender: user.fullName || "User",
        senderAccount: "Savings",
        beneficiary: "Loan",
        bankName: "Sky High NTB",
        beneficiaryAccount: loanData?.otherBanks || "N/A",
        amount: Number(loanData?.amount) || 0,
        date: moment().format('MMMM D, YYYY'),
        type: "credit",
        status: "Completed",
        userId,
        transactionIndex: existingTransactions.length,
      });
      setSuccessMessage('Loan approved and credited successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(`Error approving loan: ${err.message}`);
    } finally {
      setIsApproving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this loan?")) return;
    setIsDeleting(true);
    setError('');
    try {
      await updateDoc(doc(db, 'users', userId), { loanDetails: null });
      setUser({ ...user, loanDetails: null });
      setEditMode(false);
      setSuccessMessage('Loan deleted');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(`Error deleting: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="profile-grid detail-container">
      <div className="profile-field">
        <b className="profile-label">Amount</b>
        {editMode ? (
          <input type="text" className="profile-input" name="amount" value={updated.amount} onChange={handleChange} />
        ) : (
          <div className="profile-value">{loanData?.amount ?? 'N/A'}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Date</b>
        {editMode ? (
          <input
            type="datetime-local"
            className="profile-input"
            name="date"
            value={updated.date ? moment(updated.date).format('YYYY-MM-DDTHH:mm') : ''}
            onChange={handleChange}
          />
        ) : (
          <div className="profile-value">
            {loanData?.date ? moment(loanData.date).format('MMMM D, YYYY') : 'N/A'}
          </div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Duration</b>
        {editMode ? (
          <input type="text" className="profile-input" name="duration" value={updated.duration} onChange={handleChange} />
        ) : (
          <div className="profile-value">{loanData?.duration || 'N/A'}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Other Banks</b>
        {editMode ? (
          <input type="text" className="profile-input" name="otherBanks" value={updated.otherBanks} onChange={handleChange} />
        ) : (
          <div className="profile-value">{loanData?.otherBanks || 'N/A'}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Reason</b>
        {editMode ? (
          <textarea className="profile-input detail-textarea" name="reason" value={updated.reason} onChange={handleChange} rows={3} />
        ) : (
          <div className="profile-value">{loanData?.reason || 'N/A'}</div>
        )}
      </div>

      <div className="profile-field">
        <b className="profile-label">Status</b>
        {editMode ? (
          <select name="status" className="profile-input" value={updated.status} onChange={handleChange}>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="declined">Declined</option>
          </select>
        ) : (
          <div className="profile-value detail-text-capitalize">{loanData?.status || 'N/A'}</div>
        )}
      </div>

      <div className="detail-btn-group">
        {editMode ? (
          <>
            <button onClick={handleSave} className="profile-btn" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <button onClick={() => setEditMode(false)} className="detail-btn-cancel">
              Cancel
            </button>
          </>
        ) : (
          <button onClick={() => setEditMode(true)} className="profile-btn">Edit Loan Details</button>
        )}
        {!editMode && loanData && (
          <>
            <button onClick={handleApprove} disabled={isApproving} className="profile-btn" style={{ background: '#20B2AA', border: 'none' }}>
              {isApproving ? 'Approving...' : 'Approve'}
            </button>
            <button onClick={handleDelete} disabled={isDeleting} className="detail-btn-delete">
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </>
        )}
      </div>

      {successMessage && <div className="detail-success">{successMessage}</div>}
      {error && <div className="detail-error">{error}</div>}
    </div>
  );
};

export default UserLoanDetails;